
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { contractFormSchema, ContractFormValues } from "@/components/contracts/form/ContractFormSchema";
import { useContractFormState } from "@/components/contracts/form/useContractFormState";
import { CompanyDetails } from "@/lib/api/companyRegister";
import { useFormSubmission } from "./contract-form/useFormSubmission";
import { useNavigationHandling } from "./contract-form/useNavigationHandling";
import { useServicesManagement } from "./contract-form/useServicesManagement";
import { useTranslationWrapper } from "./contract-form/useTranslation";
import { useEffect } from "react";
import { addYears } from "date-fns";

export function useContractForm(contract?: any, mode: "create" | "edit" = "create") {
  const { translateWrapper } = useTranslationWrapper();
  
  const {
    selectedContacts, setSelectedContacts,
    attachments, setAttachments,
    existingAttachments, setExistingAttachments,
    contractTypeModalOpen, setContractTypeModalOpen,
    categoryModalOpen, setCategoryModalOpen,
    contactModalOpen, setContactModalOpen,
    currencyModalOpen, setCurrencyModalOpen,
    selectedContractType, setSelectedContractType,
    renewalDate,
    formDefaultValues,
    handleContractTypeCreated,
    handleCategoryCreated,
    handleContactCreated,
    handleCurrencyCreated,
    handleContactSelection,
    handleCompanySelect
  } = useContractFormState(contract);

  const form = useForm<ContractFormValues>({
    resolver: zodResolver(contractFormSchema),
    defaultValues: formDefaultValues,
  });

  // Services management hook
  const { 
    servicesTotal, 
    handleServicesTotalUpdate 
  } = useServicesManagement();

  // Form submission hook
  const { 
    isSubmitting, 
    isPending, 
    onSubmit: submitForm, 
    handleServicesApplyCallback 
  } = useFormSubmission(mode, contract);

  // Navigation handling hook
  const {
    isDirty,
    showUnsavedChangesDialog,
    setShowUnsavedChangesDialog,
    pendingNavigation,
    setPendingNavigation,
    handleCancelWithConfirmation,
    confirmNavigation,
    cancelNavigationDialog
  } = useNavigationHandling(form, translateWrapper);

  // Check and set a valid expiryDate when contract type changes
  useEffect(() => {
    if (selectedContractType?.auto_renews && renewalDate) {
      // If auto-renewal is enabled and we have a renewal date, use it
      form.setValue("expiryDate", renewalDate);
    } else if (mode === "create" && !form.getValues("expiryDate")) {
      // For new contracts without an expiry date, set a default one year from now
      const signedDate = form.getValues("signedDate") || new Date();
      const defaultExpiryDate = addYears(new Date(signedDate), 1);
      form.setValue("expiryDate", defaultExpiryDate);
    }
  }, [selectedContractType, renewalDate, form, mode]);

  const handleCompanySelectWithFormUpdate = (company: CompanyDetails) => {
    form.setValue("supplierName", company.name, { shouldValidate: true });
    if (company.organizationNumber) {
      form.setValue("organisationnumber", company.organizationNumber, { shouldValidate: true });
    }
    
    return handleCompanySelect(company);
  };

  const onSubmit = (data: ContractFormValues) => {
    return submitForm(data, selectedContacts, attachments, selectedContractType);
  };

  return {
    form,
    selectedContractType,
    setSelectedContractType,
    selectedContacts,
    setSelectedContacts,
    attachments,
    setAttachments,
    existingAttachments,
    setExistingAttachments,
    contractTypeModalOpen,
    setContractTypeModalOpen,
    categoryModalOpen,
    setCategoryModalOpen,
    contactModalOpen,
    setContactModalOpen,
    currencyModalOpen,
    setCurrencyModalOpen,
    renewalDate,
    servicesTotal,
    handleContractTypeCreated,
    handleCategoryCreated,
    handleContactCreated,
    handleCurrencyCreated,
    handleCompanySelectWithFormUpdate,
    handleServicesTotalUpdate,
    handleServicesApplyCallback,
    onSubmit,
    handleCancelWithConfirmation,
    isSubmitting,
    isPending,
    showUnsavedChangesDialog,
    setShowUnsavedChangesDialog,
    pendingNavigation,
    setPendingNavigation,
    confirmNavigation,
    cancelNavigationDialog,
    translateWrapper,
    isDirty
  };
}
